import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  GraduationCap,
  Users,
  Sparkles,
  ClipboardCheck,
  BookOpen,
  Compass,
  Heart,
  Handshake,
  CheckCircle2,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";

const TRACKS = [
  {
    eyebrow: "Track One",
    title: "Workplace Chaplain Development",
    designedFor:
      "Individuals interested in becoming workplace/corporate chaplains or developing foundational workplace-chaplaincy skills.",
    focus: [
      "Workplace chaplaincy fundamentals",
      "Ministry of presence and active listening",
      "Confidentiality and professional boundaries",
      "Supporting diverse employees and families",
      "Grief, crisis, serious illness, and workplace-care fundamentals",
      "Professional communication, ethics, and appropriate referral",
    ],
    nextStep: "Apply to the Chaplain Training Academy.",
    cta: "Apply for Workplace Chaplain Development",
    ctaTo: "/academy/apply",
    icon: GraduationCap,
    testid: "academy-track-workplace-development",
  },
  {
    eyebrow: "Track Two",
    title: "Continuing Education & Professional Development",
    designedFor:
      "Current chaplains, pastors, ministers, spiritual-care professionals, and experienced helping professionals seeking to strengthen practical workplace-care skills.",
    focus: [
      "Advanced workplace-care skills",
      "Grief and bereavement support",
      "Crisis and critical-incident awareness",
      "Compassion fatigue and burnout",
      "Trauma and moral-injury awareness",
      "Leadership and organizational support",
      "Professional boundaries, ethics, and referral",
      "Continuing skill development for workplace chaplaincy",
    ],
    nextStep: "Apply and identify your continuing-education interests.",
    cta: "Apply for Continuing Development",
    ctaTo: "/academy/apply",
    icon: Sparkles,
    testid: "academy-track-continuing-education",
  },
  {
    eyebrow: "Track Three",
    badge: "Book-Based Specialty Track",
    title: "Hospice Chaplaincy",
    subtitle: "Practicing From Diagnosis to Goodbye",
    designedFor:
      "Pastors, chaplains, ministers, and spiritual-care providers who want to take the principles presented in From Diagnosis to Goodbye and learn how to practice them in real hospice ministry.",
    description:
      "From the book to the bedside — learn to apply patient-centered hospice chaplaincy principles throughout the end-of-life journey.",
    focus: [
      "Applied patient-centered hospice chaplaincy",
      "Presence with patients and families through decline",
      "Anticipatory grief and family-system dynamics",
      "End-of-life conversations without a preaching posture",
      "Coordinating with hospice interdisciplinary teams",
      "Bereavement support after the death",
    ],
    nextStep: "Explore the dedicated Track Three specialty page.",
    cta: "Explore Track Three",
    ctaTo: "/academy/hospice-chaplaincy",
    freePreview: { code: "HOSP-001", to: "/academy/portal/hosp-001", label: "Free Preview Available: HOSP-001" },
    icon: Heart,
    testid: "academy-track-hospice-chaplaincy",
  },
];

const FOCUS_AREAS = [
  "Workplace and corporate chaplaincy fundamentals",
  "Ministry of presence and active listening",
  "Confidentiality and professional boundaries",
  "Grief and bereavement support",
  "Serious illness and end-of-life support",
  "Workplace stress and emotional support",
  "Compassion fatigue and burnout",
  "Suicide awareness and appropriate referral",
  "Trauma, moral injury, and PTSD awareness",
  "Domestic violence and abuse awareness",
  "Substance-use concerns and appropriate referral",
  "Workplace conflict and de-escalation",
  "Crisis and critical-incident response",
  "Supporting managers and organizational leaders",
  "Hospital and family visitation",
  "Ethical decision-making",
  "Cultural and belief-system sensitivity",
  "Professional communication",
  "Knowing when to refer to an EAP, licensed therapist, medical provider, emergency service, or other professional resource",
];

const WHO = [
  {
    icon: Compass,
    title: "Individuals exploring workplace chaplaincy",
    body: "You want to learn what workplace chaplaincy is and whether the professional path is right for you.",
  },
  {
    icon: Users,
    title: "Aspiring workplace chaplains",
    body: "You are pursuing a professional path into corporate or workplace chaplaincy and want structured development.",
  },
  {
    icon: BookOpen,
    title: "Experienced chaplains and helping professionals",
    body: "You are already a chaplain, pastor, minister, or spiritual-care professional strengthening workplace-care skills.",
  },
  {
    icon: Heart,
    title: "Future CCCHAPS candidates",
    body: "You would like to be considered for future California Corporate Chaplains opportunities as they become available.",
  },
];

const STEPS = [
  { n: "01", title: "Submit your application", body: "Share your background, primary reason for applying, training interests, and optional résumé." },
  { n: "02", title: "Application review", body: "Our team reviews each application individually. We follow up as we work through the pipeline." },
  { n: "03", title: "Conversation and next steps", body: "Qualified applicants are contacted to discuss fit, training focus, and next steps in the Academy." },
];

export default function AcademyLanding() {
  usePageMeta({
    title: "Chaplain Training Academy | California Corporate Chaplains",
    description:
      "The CCCHAPS Chaplain Training Academy is active and accepting applications. Training future workplace chaplains and strengthening experienced chaplains.",
  });

  return (
    <div data-testid="academy-landing" className="bg-white">
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F0F4F8] via-[#F8FAFC] to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col items-start gap-6 max-w-3xl">
            <span
              data-testid="academy-status-badge"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Applications Now Open
            </span>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-500 font-semibold">
              CCCHAPS Academy
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              Chaplain Training Academy
            </h1>
            <p className="text-lg sm:text-xl text-slate-700 leading-relaxed max-w-2xl font-medium">
              Training future workplace chaplains. Strengthening experienced chaplains.
            </p>
            <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
              The California Corporate Chaplains Chaplain Training Academy is a professional development program focused on the practical skills, judgment, and character required to serve well in workplace and corporate environments.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link
                to="/academy/portal"
                data-testid="academy-hero-portal-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 shadow-sm"
              >
                Start Free CTA-001 Course <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/academy/apply"
                data-testid="academy-hero-apply-btn"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
              >
                Apply to the Chaplain Training Academy <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#academy-overview"
                className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
              >
                Explore the Academy
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-2 max-w-xl leading-relaxed">
              The Academy is active and accepting applications. Additional courses, learning formats, and professional-development opportunities will continue to be added as the Academy grows.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- OVERVIEW ---------------- */}
      <section id="academy-overview" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-1">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
              About the Academy
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
              A professional development pathway for workplace chaplaincy.
            </h2>
          </div>
          <div className="lg:col-span-2 space-y-5 text-slate-700 leading-relaxed">
            <p>
              Corporate and workplace chaplaincy is a distinct discipline. It calls for calm presence, disciplined confidentiality, cultural humility, sound referral judgment, and a professional posture that fits the workplace — not the pulpit and not the therapy room.
            </p>
            <p>
              The CCCHAPS Chaplain Training Academy is designed to develop those exact skills. Whether you are exploring workplace chaplaincy for the first time or you are an experienced chaplain strengthening your workplace-care skill set, the Academy meets you where you are and grows you toward professional workplace practice.
            </p>
            <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5 flex gap-3">
              <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700 leading-relaxed">
                The Academy is currently accepting applications. Additional courses, learning formats, and professional-development opportunities will continue to be added as the Academy grows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- TRACKS AT A GLANCE ---------------- */}
      <section className="bg-[#0F172A]" data-testid="academy-tracks-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-3">
              Three Academy Tracks
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white leading-tight">
              Three Tracks at a Glance
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Choose the pathway that fits your season of professional development. The review team will confirm the best-fit track with you after your application.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TRACKS.map((t) => (
              <div
                key={t.testid}
                data-testid={t.testid}
                className="flex flex-col rounded-2xl border border-slate-800 bg-slate-900/60 p-7 hover:border-[#4A6FA5]/60 transition-colors"
              >
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#4A6FA5]/20 text-[#8CB0DA] mb-5">
                  <t.icon className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {t.badge && (
                  <span
                    data-testid={`${t.testid}-badge`}
                    className="self-start inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 border border-amber-300/40 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-200 mb-3"
                  >
                    {t.badge}
                  </span>
                )}
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#8CB0DA] font-semibold">
                  {t.eyebrow}
                </p>
                <h3 className="mt-1 font-heading text-2xl text-white leading-tight">{t.title}</h3>
                {t.subtitle && (
                  <p className="mt-1 text-sm text-slate-300 italic leading-snug">{t.subtitle}</p>
                )}
                {t.description && (
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{t.description}</p>
                )}

                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5">
                    Designed for
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">{t.designedFor}</p>
                </div>

                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-2">
                    Primary focus
                  </p>
                  <ul className="space-y-1.5">
                    {t.focus.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed">
                        <CheckCircle2 className="w-4 h-4 text-[#8CB0DA] shrink-0 mt-0.5" strokeWidth={2} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5">
                    Best next step
                  </p>
                  <p className="text-sm text-slate-300 leading-relaxed">{t.nextStep}</p>
                </div>

                <div className="mt-auto pt-6">
                  <Link
                    to={t.ctaTo || "/academy/apply"}
                    data-testid={`${t.testid}-cta`}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-white text-[#0F172A] text-sm font-medium hover:bg-[#8CB0DA] hover:text-[#0F172A] transition-colors w-full sm:w-auto justify-center"
                  >
                    {t.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                  {t.freePreview && (
                    <Link
                      to={t.freePreview.to}
                      data-testid={`${t.testid}-free-preview`}
                      className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[#8CB0DA] hover:text-white font-semibold"
                    >
                      {t.freePreview.label} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate-400 leading-relaxed max-w-3xl" data-testid="academy-schedule-note">
            Training schedules and specific learning opportunities are communicated as Academy offerings are scheduled.
          </p>
        </div>
      </section>

      {/* ---------------- BOOK-BASED COURSE (IN DEVELOPMENT) ---------------- */}
      <section
        className="bg-[#0B1220] border-t border-slate-800/70"
        data-testid="academy-book-course-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-3">
              Book-Based Course
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-white leading-tight">
              A companion course drawn from the book.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              A separate, book-based course being developed alongside the three Academy tracks. Distinct from Track Three&nbsp;Hospice&nbsp;Chaplaincy, which remains the deeper specialty pathway for spiritual-care providers.
            </p>
          </div>

          <div className="mt-10">
            <div
              data-testid="academy-book-course-card"
              className="rounded-2xl border border-slate-800 bg-slate-900/60 p-7 md:p-9 hover:border-[#4A6FA5]/60 transition-colors"
            >
              <div className="grid md:grid-cols-12 gap-8 md:gap-10">
                {/* Left column — identity + meta */}
                <div className="md:col-span-5 lg:col-span-4">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#4A6FA5]/20 text-[#8CB0DA] mb-5">
                    <BookOpen className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <span
                    data-testid="academy-book-course-status-badge"
                    className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/10 border border-amber-300/40 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-amber-200 mb-4"
                  >
                    In Development
                  </span>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#8CB0DA] font-semibold">
                    Five-Session Course
                  </p>
                  <h3
                    className="mt-1 font-heading text-2xl md:text-[1.65rem] text-white leading-tight"
                    data-testid="academy-book-course-title"
                  >
                    The Patient&rsquo;s Voice in Care
                  </h3>
                  <p
                    className="mt-1 text-sm text-slate-300 italic leading-snug"
                    data-testid="academy-book-course-subtitle"
                  >
                    Bringing the Patient&rsquo;s Voice Into Hospice and Healthcare Practice
                  </p>
                  <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                    A five-session course based on <em>From Diagnosis to Goodbye</em> that helps hospice and healthcare teams strengthen patient-centered communication, preserve dignity, and keep the person&rsquo;s voice present throughout serious illness, end-of-life care, and the transition into grief.
                  </p>
                </div>

                {/* Right column — designed for, focus, best next step */}
                <div className="md:col-span-7 lg:col-span-8 md:border-l md:border-slate-800/70 md:pl-10">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5">
                      Designed for
                    </p>
                    <p
                      className="text-sm text-slate-300 leading-relaxed"
                      data-testid="academy-book-course-designed-for"
                    >
                      Nurses, social workers, hospice aides, chaplains, care coordinators, volunteers, and other healthcare professionals who want to keep the person&rsquo;s voice present throughout serious illness and end-of-life care.
                    </p>
                  </div>

                  <div className="mt-6">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-2">
                      Primary focus
                    </p>
                    <ul className="space-y-1.5" data-testid="academy-book-course-focus-list">
                      {[
                        "Seeing the person beyond the diagnosis",
                        "Listening for concerns and wishes that may go unspoken",
                        "Understanding what may be happening beneath family and patient interactions",
                        "Reflecting on how presence and communication shape care",
                        "Turning reflection into practical, patient-centered conversations",
                      ].map((f) => (
                        <li
                          key={f}
                          className="flex items-start gap-2 text-sm text-slate-300 leading-relaxed"
                        >
                          <CheckCircle2
                            className="w-4 h-4 text-[#8CB0DA] shrink-0 mt-0.5"
                            strokeWidth={2}
                          />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-6">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold mb-1.5">
                      Best next step
                    </p>
                    <p className="text-sm text-slate-300 leading-relaxed">
                      The course is in development. Organizations interested in future team training can contact us to start a conversation.
                    </p>

                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
                      <Link
                        to="/contact"
                        data-testid="academy-book-course-inquiry-cta"
                        className="inline-flex items-center gap-2 px-4 py-3 rounded-lg bg-white text-[#0F172A] text-sm font-medium hover:bg-[#8CB0DA] hover:text-[#0F172A] transition-colors justify-center"
                      >
                        Ask about future team training <ArrowRight className="w-4 h-4" />
                      </Link>
                      <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
                        Not yet enrolling
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- WHO ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
            Who the Academy is for
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
            Professional development for a range of applicants.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            The Academy is open to qualified applicants across the following broad categories. You do not need to identify with a particular role or title to apply.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHO.map((w) => (
            <div
              key={w.title}
              className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-sm transition-all"
              data-testid={`academy-who-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-4">
                <w.icon className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg text-slate-900 leading-tight">{w.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- FOCUS AREAS ---------------- */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
                Training Focus Areas
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
                Practical, workplace-tested skills.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                The Academy focuses on the disciplines that matter most in workplace and corporate environments. Additional training areas will be added as the Academy grows.
              </p>
            </div>
            <div className="lg:col-span-2">
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="academy-focus-list">
                {FOCUS_AREAS.map((area) => (
                  <li key={area} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                    <CheckCircle2 className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{area}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- HOW TO APPLY ---------------- */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">
            How to Apply
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
            A simple, professional application process.
          </h2>
        </div>
        <ol className="grid md:grid-cols-3 gap-5">
          {STEPS.map((s) => (
            <li key={s.n} className="rounded-2xl border border-slate-200 bg-white p-6">
              <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold">Step {s.n}</p>
              <h3 className="mt-2 font-heading text-xl text-slate-900 leading-tight">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ---------------- EMPLOYMENT DISTINCTION + DISCLAIMER ---------------- */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7" data-testid="academy-vs-employment">
              <div className="inline-flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-5 h-5 text-[#4A6FA5]" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                  Academy vs. Employment
                </p>
              </div>
              <h3 className="font-heading text-xl text-slate-900 leading-snug">
                Applying to the Academy is <em>not</em> applying for a job.
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                The Academy is a professional-development program. To apply for a specific CCCHAPS position, please use the{" "}
                <Link to="/careers" className="text-[#4A6FA5] hover:text-[#3B597B] underline underline-offset-2">
                  Careers page
                </Link>
                . Academy applicants who indicate interest in future CCCHAPS chaplain opportunities will be reviewed separately as roles become available.
              </p>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-6 md:p-7" data-testid="academy-employment-disclaimer">
              <div className="inline-flex items-center gap-2 mb-3">
                <ShieldCheck className="w-5 h-5 text-amber-800" strokeWidth={1.75} />
                <p className="text-xs uppercase tracking-[0.2em] text-amber-900 font-semibold">
                  Employment Disclaimer
                </p>
              </div>
              <p className="text-sm text-amber-900 leading-relaxed">
                Academy application, participation, or completion does not guarantee employment, placement, assignment, deployment, or a position with California Corporate Chaplains. Employment decisions remain separate and are based on organizational needs, qualifications, screening, and applicable hiring requirements.
              </p>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500 leading-relaxed max-w-4xl" data-testid="academy-legal-note">
            The CCCHAPS Chaplain Training Academy is a private professional-development program. It does not claim accreditation, state approval, academic credit, professional certification, or licensure. Applying to the Academy does not guarantee Academy acceptance.
          </p>
        </div>
      </section>

      {/* ---------------- FINAL CTA ---------------- */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#F0F4F8] text-[#4A6FA5] mb-5">
          <Handshake className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900 leading-tight">
          Ready to apply to the CCCHAPS Chaplain Training Academy?
        </h2>
        <p className="mt-4 text-slate-600 leading-relaxed">
          Applications are open. Share your background and training interests — a member of our team will review your application and follow up.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/academy/apply"
            data-testid="academy-final-apply-btn"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] shadow-sm"
          >
            Apply to the Chaplain Training Academy <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
          >
            Contact us with questions
          </Link>
        </div>
      </section>
    </div>
  );
}
